import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Button, Container } from 'react-bootstrap';
import Filter from '../components/Filter';
import { GridColDef, GridRenderCellParams, GridSortModel } from '@mui/x-data-grid';
import { useCargo } from '../../../apis/cargo/CargoProvider';
import { useCargoSearchList } from '../../../apis/cargoSearchList/CargoSearchListProvider';
import { getPageName, getUiInterface } from '../../../locales/translationUtils';
import { useTranslation } from 'react-i18next';
//import createIcon from "../../../assets/create-icon.png"; // Ajuste o caminho conforme necessário
import i18n from '../../../i18n';
import { useNavigate } from 'react-router-dom';
import NavBar, { NavBarItem } from '../../../components/NavBar';
import CargoTablePaginated from '../components/CargoTablePaginated';
import CargoContentList from '../../../components/CargoContentList';
import DocumentPreviewModal from '../../../components/DocumentPreviewModal';
import { useDocumentPreview } from '../../../hooks/useDocumentPreview';
import { Content } from '../../Release/types';
import { downloadContent } from '../../../services/contentService';
import { getListaCargo } from '../../../services/cargoService';
import { exportarParaExcel } from '../../../utils/exportarExcel';
import ExportExcelButton from '../../../components/ExportExcelButton';
import AlertOverlay from '../../../components/Alert';

// A API so aplica o filtro de PU quando inicio E fim estao presentes
// (CargoDAO.FindByDinamicFilter exige os dois, mesmo padrao usado no Release).
// Para expressar "PU >= 01/01/2021" sem limite superior, o fim usa uma data
// bem no futuro - nunca alcancada na pratica.
const PU_FILTRO_DATA_MINIMA = '2021-01-01';
const PU_FILTRO_DATA_MAXIMA_SEM_LIMITE = '2099-12-31';

const CargoListView: React.FC = () => {
  const { t } = useTranslation();
  const [alert, setAlert] = useState<{
    message: string;
    variant: 'success' | 'danger' | 'warning' | 'info';
  } | null>(null);
  const navigate = useNavigate();
  const { cargos, fetchCargos, apiResponse } = useCargo();
  const { cargosSearchList, fetchCargoSearchList } = useCargoSearchList();
  const { previewState, openPreview, closePreview } = useDocumentPreview();
  const [isReleased, setIsReleased] = useState(false);
  const [exportando, setExportando] = useState(false);
  // Ligado por padrao: a tela sempre abre mostrando so processos recentes.
  const [filtrarPuApartir2021, setFiltrarPuApartir2021] = useState(true);
  const [filters, setFilters] = useState<Record<string, any>>({
    puInicio: PU_FILTRO_DATA_MINIMA,
    puFim: PU_FILTRO_DATA_MAXIMA_SEM_LIMITE,
  });

  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 40,
    page: 0,
  });

  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);

  useEffect(() => {
    fetchCargoSearchList();
  }, [fetchCargoSearchList]);

  useEffect(() => {
    if (cargosSearchList && cargosSearchList.length > 0) {
      const initialFilters = Object.fromEntries(
        cargosSearchList.map(({ key }: { key: string }) => [key, ''])
      );
      initialFilters.pageNum = "0";
      initialFilters.quantityPerPage = "40";
      initialFilters.puInicio = filtrarPuApartir2021 ? PU_FILTRO_DATA_MINIMA : '';
      initialFilters.puFim = filtrarPuApartir2021 ? PU_FILTRO_DATA_MAXIMA_SEM_LIMITE : '';

      setFilters((prevFilters) => {
        if (JSON.stringify(prevFilters) !== JSON.stringify(initialFilters)) {
          return initialFilters;
        }
        return prevFilters;
      });
    }
    // filtrarPuApartir2021 entra aqui com seguranca: e um booleano que so muda
    // por acao do usuario, nao por este efeito, entao nao cria loop.
  }, [cargosSearchList, filtrarPuApartir2021]);

  const dateFormatter = new Intl.DateTimeFormat(i18n?.language ?? 'pt');

  /**
   * Exporta para Excel todos os itens que batem com o filtro atual - nao so a
   * pagina visivel. Busca direto do servico, sem tocar no estado do CargoProvider
   * que alimenta a grid.
   */
  const handleExportarExcel = async () => {
    setExportando(true);

    try {
      const filtrosParaExportar = {
        ...filters,
        pageNum: '0',
        quantityPerPage: '100000',
      };

      const resposta = await getListaCargo(filtrosParaExportar as any, isReleased);
      const linhas = (resposta?.data?.values ?? []).map((row: any) => {
        const info = row?.carrierInformation?.[0];
        return {
          [t('cargo.fields.id')]: row.id,
          [t('cargo.fields.from')]: row.fromIdentification ?? '',
          [t('cargo.fields.client')]: row.clientName ?? '',
          [t('cargo.fields.supplier')]: row.supplierName ?? '',
          [t('cargo.fields.piecesWeight')]: `${row.pieces ?? ''} / ${row.weightRaw ?? row.weight ?? ''}`,
          [t('cargo.fields.dawb')]: row.dawb ?? '',
          [t('cargo.fields.pu')]: row.pu ? dateFormatter.format(new Date(row.pu)) : '',
          [t('cargo.fields.wrNumber')]: row.wrNumber ?? '',
          [t('cargo.fields.docs')]: `${info?.invoice ?? '-'} / ${info?.document?.key ?? '-'}`,
        };
      });

      if (!(await exportarParaExcel(linhas, isReleased ? 'cargo_released' : 'cargo_info'))) {
        setAlert({ message: getUiInterface('exportEmptyResult'), variant: 'warning' });
      }
    } catch (error) {
      setAlert({ message: getUiInterface('exportError'), variant: 'danger' });
    } finally {
      setExportando(false);
    }
  };

  useEffect(() => {
    const newFilters = {
      ...filters,
      pageNum: paginationModel.page.toString(),
      quantityPerPage: paginationModel.pageSize.toString(),
      paginacao: {
        sort: sortModel[0]?.field,
        order: sortModel[0]?.sort ?? undefined,
      },
    };
    setFilters(newFilters);
    fetchCargos(newFilters, isReleased);
  }, [paginationModel, sortModel]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = () => {
    const newFilters = { 
      ...filters, 
      pageNum: "0",
      quantityPerPage: "40"
    };
    setFilters(newFilters);
    fetchCargos(newFilters, isReleased);
  };

  /**
   * Liga/desliga o filtro de PU sem exigir um clique em "Pesquisar" - e um
   * checkbox, a resposta precisa ser imediata.
   */
  const handleToggleFiltrarPu = (checked: boolean) => {
    setFiltrarPuApartir2021(checked);
    const newFilters = {
      ...filters,
      pageNum: "0",
      puInicio: checked ? PU_FILTRO_DATA_MINIMA : '',
      puFim: checked ? PU_FILTRO_DATA_MAXIMA_SEM_LIMITE : '',
    };
    setFilters(newFilters);
    fetchCargos(newFilters, isReleased);
  };
    
    const handleDownload = async (content: Pick<Content, 'id' | 'description'>) => {
      try {
        const response = await downloadContent(content.id);
        
        const downloadUrl = window.URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `${content.description}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
      } catch (error) {
          setAlert({ message: `${t('cargo.messages.downloadError')} ${content.description}`, variant: "danger" });
      }
    };

    // E5: visualizar PDF/imagem direto no navegador, sem baixar antes.
    const handleView = async (content: Pick<Content, 'id' | 'description'>) => {
      try {
        await openPreview(content);
      } catch (error) {
        setAlert({ message: `${t('cargo.messages.downloadError')} ${content.description}`, variant: "danger" });
      }
    };

  const columns: GridColDef[] = [
    {
      field: 'visualizar',
      headerName: '',
      width: 80,
      editable: false,
      sortable: false,
      hideSortIcons: true,
      disableColumnMenu: true,
      renderCell: ({ row: { id } }: GridRenderCellParams) => (
        <Button variant="secondary" onClick={() => navigate(`cargo/${id}`)}>
          <span className="bi bi-search"></span>
        </Button>
      ),
    },
    { field: 'id', headerName: t('cargo.fields.id'), width: 80 },
    { field: 'fromIdentification', headerName: t('cargo.fields.from'), width: 80 },
    { field: 'clientName', headerName: t('cargo.fields.client'), width: 120 },
    { field: 'supplierName', headerName: t('cargo.fields.supplier'), width: 450 },
    {
      field: 'piecesWeight',
      headerName: t('cargo.fields.piecesWeight'),
      width: 120,
      renderCell: (params: any) => {
        const { pieces, weight, weightRaw } = params.row;
        return `${pieces} / ${weightRaw ?? weight}`;
      },
    },
    { field: 'dawb', headerName: t('cargo.fields.dawb'), width: 100 },
    {
      field: 'pu',
      headerName: t('cargo.fields.pu'),
      type: 'date',
      width: 100,
      valueFormatter: (params: any) => {
        if (!params) return null;
        const date = new Date(params);
        return isNaN(date.getTime()) ? null : dateFormatter.format(date);
      },
    },
    { field: 'wrNumber', headerName: t('cargo.fields.wrNumber'), width: 100 },
    {
      field: 'docs',
      headerName: t('cargo.fields.docs'),
      width: 220,
      renderCell: (params: any) => {
        const info = params?.row?.carrierInformation?.[0];
        const invoice = info?.invoice ?? '-';
        const documentKey = info?.document?.key ?? '-';
        return `${invoice} / ${documentKey}`;
      }
    },
    {
      field: 'content',
      headerName: t('cargo.fields.content'),
      width: 500,
      minWidth: 200,
      renderCell: (params: any) => (
        <CargoContentList
          cargoContents={params.row?.cargoContents ?? []}
          onDownload={handleDownload}
          downloadTitle={t('cargo.labels.clickToDownload')}
          expandTitle={t('cargo.labels.showAllFiles')}
          onView={handleView}
          viewTitle={t('cargo.labels.clickToView')}
        />
      )
    }

  ];

  const navItems: NavBarItem[] = [
    { label: t('cargo.labels.home'), route: '/', iconName: 'house-door' },
    {
      label: t('cargo.labels.newCargoInfo'),
      route: '/cargo-info/create',
      iconName: 'database-add',
    },
    {
      label: t('cargo.labels.releasedList'),
      route: '/cargo-info/',
      iconName: 'table',
      onClick: () => {
        fetchCargoSearchList();
        fetchCargos({
          pageNum: "0",
          quantityPerPage: "40"
        }, true);
        setIsReleased(true);
      },
      isHidden: isReleased,
    },
    {
      label: t('cargo.labels.cargoInfoList'),
      route: '/cargo-info/',
      iconName: 'table',
      onClick: () => {
        fetchCargoSearchList();
        fetchCargos({
          pageNum: "0",
          quantityPerPage: "40"
        }, false);
        setIsReleased(false);
      },
      isHidden: !isReleased,
    },
    {
      label: t('cargo.labels.supplierList'),
      route: '/supplier/',
      iconName: 'info-circle',
    },
  ];

  return (
    <>
      <NavBar items={navItems} />
      {alert && (
        <AlertOverlay
          message={alert.message}
          variant={alert.variant}
          onClose={() => setAlert(null)}
        />
      )}
      <Container
        fluid
        style={{ height: 'calc(100vh - 150px)', display: 'flex', flexDirection: 'column' }}
      >
        <h2 className="mt-4">
          {isReleased
            ? getPageName('cargoInfoReleased')
            : getPageName('cargoInfo')}
        </h2>
        <Filter
          filters={filters}
          onFilterChange={handleFilterChange}
          onSearch={handleSearch}
          filtrarPuApartir2021={filtrarPuApartir2021}
          onToggleFiltrarPuApartir2021={handleToggleFiltrarPu}
        />
        <div className="d-flex justify-content-end mb-2">
          <ExportExcelButton onClick={handleExportarExcel} exportando={exportando} />
        </div>
        <div style={{ flex: 1, minHeight: 0 }}>
          <CargoTablePaginated
            apiResponse={apiResponse}
            rows={cargos ?? []}
            columns={columns}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
          />
        </div>

        <DocumentPreviewModal state={previewState} onClose={closePreview} />
      </Container>
    </>
  );
};

export default CargoListView;
